import useCustomMutation from "@/composables/useCustomMutation";
import { Comment } from "@/interfaces/comment";
import { MutationParams } from "@/interfaces/mutationParams";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { useToast } from "vue-toastification";

interface Params {
  companyId?: string | number;
}

interface Props {
  params: Params;
  immediate?: boolean;
}

interface CreateParams {
  creatorId: string | number;
  companyId: string | number;
  commentary: string;
}

const getCompanyComment = async (params?: Params) => {
  if (params) {
    const { data } = await axios.get(
      `api/companies/${params.companyId}/comments`,
    );
    return data;
  }
};

const createCompanyComment = async (params: CreateParams) => {
  const { data } = await axios.post(
    `api/companies/${params.companyId}/comments`,
    params,
  );
  return data;
};

const useCompanyComment = (props?: Props) => {
  const { params, immediate = true } = props || {};
  const toast = useToast();

  const { data, isLoading, refetch, isFetching } = useQuery(
    ["companyComment", params?.companyId],
    () => getCompanyComment(params),
    {
      enabled: props ? immediate : false,
    },
  );

  const createMutation = useMutation(
    (mutationParams: MutationParams<CreateParams>) =>
      createCompanyComment(mutationParams.params),
    useCustomMutation(toast, "create"),
  );

  return {
    data,
    isLoading,
    isFetching,

    refetch: () => {
      props && refetch();
    },

    createCompanyCommentMutation: createMutation,
    createCompanyComment: createMutation.mutate,
    createCompanyCommentAsync: createMutation.mutateAsync,
  };
};

export default useCompanyComment;
